import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, MapPin, Star, ChevronLeft, ChevronRight, PenLine } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import BookingWidget from '../components/BookingWidget';
import RoomCard from '../components/RoomCard';
import { usePageTitle } from '../hooks/usePageTitle';

// --- IMPOR DATABASE STATIS ---
import roomsDataRaw from '../data/rooms.json';
import promosDataRaw from '../data/promotions.json';

// --- KEBAL ERROR: Pastikan data yang masuk selalu berbentuk Array ---
const roomsList = Array.isArray(roomsDataRaw) ? roomsDataRaw : (roomsDataRaw?.default || []);
const promosList = Array.isArray(promosDataRaw) ? promosDataRaw : (promosDataRaw?.default || []);

const TESTIMONIALS_DEFAULT = [
  { guest_name: 'Budi Santoso', rating: 5, body: 'Suasananya sangat tenang dan autentik. Kolam renang dan tamannya indah sekali. Pasti balik lagi!', guest_origin: 'Surabaya' },
  { guest_name: 'Sarah Chen', rating: 5, body: 'Absolutely loved the Javanese ambiance. Staff were incredibly warm and helpful. Perfect base for Ijen trip!', guest_origin: 'Singapore' },
  { guest_name: 'Dewi Rahayu', rating: 5, body: 'Kamar bungalow-nya mewah banget untuk harganya. Sarapannya enak, menu lokalnya otentik.', guest_origin: 'Jakarta' },
];

const ATTRACTIONS = [
  { slug: 'kawah-ijen',         name: 'Kawah Ijen',          distance: '68', icon: '🌋' },
  { slug: 'pantai-pulau-merah', name: 'Pantai Pulau Merah',  distance: '55', icon: '🏖️' },
  { slug: 'pantai-boom',        name: 'Pantai Boom',         distance: '3',  icon: '🌊' },
  { slug: 'alas-purwo',         name: 'TN Alas Purwo',       distance: '90', icon: '🌿' },
  { slug: 'desa-kemiren',       name: 'Desa Wisata Kemiren', distance: '5',  icon: '🏮' },
];

export default function Home() {
  const { i18n: _pt } = useTranslation(); 
  usePageTitle(_pt.language === 'id' ? 'Beranda' : 'Home');
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  // --- BACA DATA AMAN ---
  const [rooms, setRooms]               = useState(roomsList.slice(0, 3)); 
  const [promos, setPromos]             = useState(promosList);
  const [testimonials, setTestimonials] = useState(TESTIMONIALS_DEFAULT);
  const [reviewStats, setReviewStats]   = useState({ avg_rating: 4.9, total: TESTIMONIALS_DEFAULT.length });
  const [testimonialIdx, setTestimonialIdx] = useState(0);

  const { ref: roomsRef, inView: roomsInView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const { ref: statsRef, inView: statsInView } = useInView({ triggerOnce: true, threshold: 0.2 });

  useEffect(() => {
    if (testimonials.length <= 1) return;
    const tInterval = setInterval(() => setTestimonialIdx(p => (p + 1) % testimonials.length), 6000);
    return () => clearInterval(tInterval);
  }, [testimonials.length]);

  const prevTestimonial = () => setTestimonialIdx(p => (p - 1 + testimonials.length) % testimonials.length);
  const nextTestimonial = () => setTestimonialIdx(p => (p + 1) % testimonials.length);

  const getPromoWhatsAppUrl = (promoNameId, promoNameGuest) => {
    const phoneNumber = "6281378575758";
    const adminCopy = `\n\n====================\n*Pesan Otomatis untuk Admin:*\nTamu di atas (berbahasa asing) tertarik menggunakan promo: *${promoNameId}*.`;
    
    let message = "";
    if (lang === 'id') {
      message = `Halo Ndalem Mataram,\n\nSaya melihat penawaran spesial di website dan tertarik untuk menggunakan promo: *${promoNameId}*.\n\nMohon informasinya lebih lanjut. Terima kasih.`;
    } else if (lang === 'zh') {
      message = `你好 Ndalem Mataram，\n\n我在网站上看到了特别优惠，并对使用此促销感兴趣：*${promoNameGuest}*。\n\n请提供更多信息。谢谢。` + adminCopy;
    } else {
      message = `Hello Ndalem Mataram,\n\nI saw a special offer on the website and am interested in using the promo: *${promoNameGuest}*.\n\nCould you please provide more information?\n\nThank you.` + adminCopy;
    }
    return `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
  };

  const getGeneralWhatsAppUrl = () => {
    const phoneNumber = "6281378575758";
    const adminCopy = `\n\n====================\n*Pesan Otomatis untuk Admin:*\nTamu di atas (berbahasa asing) ingin menanyakan informasi mengenai reservasi kamar secara umum.`;
    
    let message = "";
    if (lang === 'id') {
      message = "Halo Ndalem Mataram,\n\nSaya ingin menanyakan informasi mengenai reservasi kamar. Mohon bantuannya.\n\nTerima kasih.";
    } else if (lang === 'zh') {
      message = "你好 Ndalem Mataram，\n\n我想咨询有关客房预订的信息。请协助，谢谢。" + adminCopy;
    } else {
      message = "Hello Ndalem Mataram,\n\nI would like to inquire about room reservations. Could you please assist me?\n\nThank you." + adminCopy;
    }
    return `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
  };

  return (
    <div className="bg-cream overflow-x-hidden">

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: "url('/images/home.jpeg')" }}>
          <div className="absolute inset-0 bg-black/40" />
        </div>
        <div className="absolute inset-0 opacity-20 mix-blend-overlay" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.4'/%3E%3C/svg%3E\")" }} />

        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center pt-32 pb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent-500/20 border border-accent-400/30 rounded-full mb-8 backdrop-blur-sm">
            <span className="w-2 h-2 rounded-full bg-accent-400 animate-pulse" />
            <span className="text-accent-300 text-xs font-sans tracking-[0.2em] uppercase">
              {lang === 'id' ? 'Homestay Budaya Jawa · Banyuwangi' : 'Javanese Cultural Homestay · Banyuwangi'}
            </span>
          </div>
          
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl text-white font-bold leading-[1.05] mb-6">
            {lang === 'id' ? (<>Rasakan Jiwa<br /><span className="text-accent-400">Jawa</span> di Sini</>) : lang === 'zh' ? (<>感受<br /><span className="text-accent-400">爪哇</span>灵魂</>) : (<>Feel the<br /><span className="text-accent-400">Javanese</span> Soul</>)}
          </h1>
          <p className="text-white/70 text-lg md:text-xl font-body max-w-2xl mx-auto mb-10 leading-relaxed">{t('hero.subtitle')}</p>
          
          <div className="flex items-center justify-center gap-4 mb-12">
            <button 
              onClick={() => document.getElementById('booking-widget-section')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-7 py-3.5 bg-primary-600 hover:bg-primary-500 text-white font-sans font-semibold rounded-xl transition-all duration-200 hover:shadow-xl hover:shadow-primary-900/40 hover:-translate-y-0.5"
            >
              {t('hero.cta_book')}
            </button>
            <Link to="/rooms" className="flex items-center gap-2 px-7 py-3.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white font-sans font-semibold rounded-xl border border-white/20 transition-all duration-200 group">
              {t('hero.cta_explore')}<ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
          
          <div id="booking-widget-section">
            <BookingWidget variant="hero" />
          </div>
        </div>
      </section>

      {/* ── PEMBATAS WAJIK ──────────────────────────────────────────── */}
      <div className="bg-cream pt-20 pb-8 flex justify-center items-center">
        <div className="w-full max-w-2xl px-6 flex items-center">
          <div className="h-px flex-1 bg-gradient-to-r from-transparent via-earth-300 to-transparent opacity-70"></div>
          <div className="px-6 flex items-center gap-2.5">
            <div className="w-1.5 h-1.5 bg-accent-400 rotate-45 opacity-60"></div>
            <div className="w-2.5 h-2.5 border border-accent-500 rotate-45 flex items-center justify-center">
              <div className="w-1 h-1 bg-accent-500"></div>
            </div>
            <div className="w-1.5 h-1.5 bg-accent-400 rotate-45 opacity-60"></div>
          </div>
          <div className="h-px flex-1 bg-gradient-to-r from-transparent via-earth-300 to-transparent opacity-70"></div>
        </div>
      </div>

      {/* ── ROOMS ────────────────────────────────────────────── */}
      <section ref={roomsRef} className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className={`text-center mb-14 transition-all duration-700 ${roomsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <p className="text-primary-500 text-xs font-sans tracking-[0.3em] uppercase mb-3">— {lang === 'id' ? 'Tempat Beristirahat' : 'Your Sanctuary'} —</p>
            <h2 className="font-display text-4xl md:text-5xl text-dark font-bold mb-4">{t('rooms.title')}</h2>
            <p className="text-gray-500 font-body text-lg max-w-2xl mx-auto">{t('rooms.subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rooms.length > 0 ? (
              rooms.map((room, i) => (
                <div key={room.id || i} className={`transition-all duration-700 ${roomsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} style={{ transitionDelay: `${i * 150}ms` }}>
                  <RoomCard room={room} />
                </div>
              ))
            ) : (
              <div className="col-span-full text-center text-gray-400 py-10">
                Memuat data kamar...
              </div>
            )}
          </div>
          <div className="text-center mt-10">
            <Link to="/rooms" className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-500 font-sans font-semibold border-b-2 border-primary-200 hover:border-primary-500 pb-0.5 transition-colors group">
              {lang === 'id' ? 'Lihat Semua Kamar' : 'View All Rooms'}<ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── PROMOTIONS ───────────────────────────────────────── */}
      {promos.length > 0 && (
        <section className="py-24 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <p className="text-primary-500 text-xs font-sans tracking-[0.3em] uppercase mb-3">— {lang === 'id' ? 'Penawaran Spesial' : 'Special Offers'} —</p>
              <h2 className="font-display text-4xl md:text-5xl text-dark font-bold mb-4">{t('promotions.title')}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {promos.slice(0, 3).map(promo => (
                <div key={promo.id} className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-lg transition-shadow">
                  {promo.image && <img src={promo.image} alt={promo.title_id} className="w-full h-44 object-cover" loading="lazy" />}
                  <div className="p-5">
                    <div className="text-xs text-primary-600 font-sans font-semibold uppercase tracking-widest mb-2">{t('promotions.discount')} {promo.discount_percent}%</div>
                    <h3 className="font-display text-xl text-dark font-bold mb-2">{lang === 'zh' ? promo.title_zh : (lang === 'en' ? promo.title_en : promo.title_id)}</h3>
                    <p className="text-gray-500 text-sm font-sans mb-4">{lang === 'zh' ? promo.description_zh : (lang === 'en' ? promo.description_en : promo.description_id)}</p>
                    
                    <a 
                      href={getPromoWhatsAppUrl(promo.title_id, lang === 'en' ? promo.title_en : promo.title_id)} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="w-full block text-center py-2.5 bg-primary-600 hover:bg-primary-500 text-white text-sm font-sans font-semibold rounded-xl transition-colors"
                    >
                      {t('promotions.book_promo')}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── TESTIMONIALS ─────────────────────────────────────── */}
      <section className="py-24 bg-primary-950 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23d4881c' fill-opacity='0.8'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4z'/%3E%3C/g%3E%3C/svg%3E")` }} />
        <div className="relative max-w-3xl mx-auto px-6 text-center">

          <p className="text-accent-400 text-xs font-sans tracking-[0.3em] uppercase mb-4">— {lang === 'id' ? 'Kata Tamu Kami' : 'Guest Reviews'} —</p>

          <div className="flex items-center justify-center gap-2 mb-10">
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => <Star key={i} size={20} fill="#e4a52e" className="text-accent-400" />)}
            </div>
            {reviewStats.total > 0 && (
              <span className="text-white/50 font-sans text-sm ml-1">
                {reviewStats.avg_rating}/5 · {reviewStats.total} {lang === 'id' ? 'ulasan' : 'reviews'}
              </span>
            )}
          </div>

          {testimonials.length > 0 ? (
            <>
              <blockquote className="font-display text-2xl md:text-3xl text-white font-medium italic leading-relaxed mb-5 min-h-[4rem] transition-all duration-500">
                "{testimonials[testimonialIdx]?.body || ''}"
              </blockquote>
              <div className="text-white/60 font-sans text-sm mb-8 flex items-center justify-center gap-2 flex-wrap">
                <span className="text-white font-semibold">{testimonials[testimonialIdx]?.guest_name}</span>
                {testimonials[testimonialIdx]?.guest_origin && (
                  <><span className="text-white/30">·</span><span>{testimonials[testimonialIdx].guest_origin}</span></>
                )}
                {testimonials[testimonialIdx]?.rating && (
                  <span className="flex gap-0.5 ml-1">
                    {[...Array(testimonials[testimonialIdx].rating)].map((_, i) => (
                      <Star key={i} size={11} fill="#e4a52e" className="text-accent-400" />
                    ))}
                  </span>
                )}
              </div>

              <div className="flex items-center justify-center gap-3 mb-10">
                <button onClick={prevTestimonial} aria-label="Sebelumnya"
                  className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors">
                  <ChevronLeft size={18} />
                </button>
                {testimonials.map((_, i) => (
                  <button key={i} onClick={() => setTestimonialIdx(i)}
                    className={`rounded-full transition-all duration-300 ${i === testimonialIdx ? 'w-6 h-2 bg-accent-400' : 'w-2 h-2 bg-white/30 hover:bg-white/50'}`} />
                ))}
                <button onClick={nextTestimonial} aria-label="Berikutnya"
                  className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors">
                  <ChevronRight size={18} />
                </button>
              </div>
            </>
          ) : (
            <p className="text-white/40 font-sans text-lg italic mb-10">
              {lang === 'id' ? 'Jadilah yang pertama memberikan ulasan!' : 'Be the first to leave a review!'}
            </p>
          )}

        {/* GANTI BLOK ACTION BUTTONS MENJADI SEPERTI INI */}
<div className="flex items-center justify-center gap-3">
  <Link to="/reviews"
    className="inline-flex items-center gap-2 px-7 py-3 bg-white/10 hover:bg-white/20 text-white font-sans font-semibold rounded-xl transition-colors text-sm">
    {lang === 'id'
      ? `Lihat Semua Ulasan${reviewStats.total > 0 ? ` (${reviewStats.total})` : ''}`
      : `All Reviews${reviewStats.total > 0 ? ` (${reviewStats.total})` : ''}`}
    <ArrowRight size={14} />
  </Link>
</div>
        </div>
      </section>
    </div>
  );
}